import React, { useCallback, useEffect, useState } from 'react'
import api from '../../../../../services/api'
import Form, {
  Input,
  Select,
  SelectSearch,
  Textarea
} from '../../../../../components/Form'
import { useHistory } from 'react-router-dom'
import { useToast } from '../../../../../hooks/toast'
import { useLoading } from '../../../../../hooks/loading'
import { useUpdateDataTable } from '../../../../../hooks/dataTable'
import { apiUpdate } from '../../domain/api'
import {
  nameActions,
  ROUTE_PRIORITY_ENUM,
  ROUTE_PRIORITY_MAP,
  ROUTE_STATUS_ENUM,
  ROUTE_STATUS_MAP
} from '../../domain/info'
import { FormContainer } from './styles'
import { INPUT_MASK } from '../../../../../common/constants'
import { apiListPromotersOptions } from '../../../Promoters/domain/api'
import { Date as DatePicker } from '../../../../../components/Form/date'
import { IMaskInput } from 'react-imask'
import moment from 'moment'
import { listAgenciesByPromoterOptions } from '../../../Agencies/domain/api'
import { v4 } from 'uuid'

type IsOpenInModalProps = {
  idParent: number
  handleOnClose: () => void
}

type RouteData = {
  id?: string
  promoter_id: number
  name: string
  scheduled_date?: Date | string
  priority: ROUTE_PRIORITY_ENUM
  observation?: string | null
  status: ROUTE_STATUS_ENUM
  completed_at?: Date | null
  routeAgencies: {
    id?: string | number
    route_id?: number
    agency_id?: number
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
    show?: boolean
  }[]
}

type RouteResponseData = {
  route: {
    name: string
    scheduled_date?: Date
    promoter_id: number
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
    status: ROUTE_STATUS_ENUM
    completed_at?: Date | null
  }
  routeAgencies?: {
    id?: number | string
    agency_id?: number
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
    show?: boolean
  }[]
}

interface ICreateRouteWithRoutesAgenciesDTO {
  route: {
    promoter_id: number
    name: string
    scheduled_date: string
    priority: ROUTE_PRIORITY_ENUM
    status: ROUTE_STATUS_ENUM
    observation?: string | null
  }
  routeAgencies: {
    agency_id: number
    priority: ROUTE_PRIORITY_ENUM
    observation?: string | null
  }[]
}

type TypesFormProps = {
  isOpenInModal?: false | IsOpenInModalProps
  initialValues?: RouteData
}

export const FormRoute = ({
  isOpenInModal,
  initialValues
}: TypesFormProps): JSX.Element => {
  const { addToast } = useToast()
  const history = useHistory()
  const { updateDataTable } = useUpdateDataTable()

  const [scheduledDate, setScheduledDate] = useState<Date>()

  const [promotersOptions, setPromotersOptions] = useState([])
  const [agenciesOptions, setAgenciesOptions] = useState([])

  const [defaultValues, setDefaultValues] = useState<RouteResponseData>()

  const [routeAgencies, setRouteAgencies] = useState<
    RouteResponseData['routeAgencies']
  >([])

  const loadAgencies = useCallback(async (promoter_id: number) => {
    if (!promoter_id) return
    try {
      const responseAgencies = await api.get(
        listAgenciesByPromoterOptions(String(promoter_id))
      )
      setAgenciesOptions(
        responseAgencies.data?.map((item: { name: string; id: number }) => ({
          name: `${item.name}`,
          value: item.id
        }))
      )
    } catch (error) {}
  }, [])

  useEffect(() => {
    if (initialValues) {
      setRouteAgencies(
        initialValues.routeAgencies.map(item => ({
          id: item.id,
          agency_id: item.agency_id,
          priority: item.priority,
          observation: item.observation,
          show: true
        }))
      )
      setScheduledDate(moment(initialValues?.scheduled_date).toDate())
      loadAgencies(initialValues.promoter_id)
      setDefaultValues({
        route: {
          name: initialValues.name,
          scheduled_date: moment(initialValues.scheduled_date).toDate(),
          priority: initialValues.priority,
          promoter_id: initialValues.promoter_id,
          status: initialValues.status,
          observation: initialValues.observation
        },
        routeAgencies: initialValues.routeAgencies.map(item => ({
          id: item.id,
          agency_id: item.agency_id,
          priority: item.priority,
          observation: item.observation,
          show: true
        }))
      })
    }
  }, [initialValues, loadAgencies])

  const { activeLoading, disableLoading } = useLoading()

  const onSubmitForm = async (data: RouteResponseData) => {
    data.routeAgencies = data.routeAgencies.filter(item => item.agency_id)

    const dataUpdate: ICreateRouteWithRoutesAgenciesDTO = {
      route: {
        name: data.route.name,
        scheduled_date: data.route.scheduled_date.toISOString(),
        priority: data.route.priority,
        status: data.route.status,
        promoter_id: data.route.promoter_id,
        observation: data.route.observation
      },
      routeAgencies: data.routeAgencies.map(agency => ({
        id: agency.id,
        agency_id: agency.agency_id,
        priority: agency.priority,
        observation: agency.observation
      }))
    }

    try {
      try {
        activeLoading()
        await api.put(apiUpdate(initialValues.id), dataUpdate)
        disableLoading()
        updateDataTable()
        addToast({
          type: 'success',
          title: 'Registro criado',
          description: 'Registro criado com sucesso'
        })
        history.push(nameActions.read.to)
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao adicionar o registro',
          description:
            'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
        })
        disableLoading()
        updateDataTable()
      }
      disableLoading()
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro no cadastro',
        description:
          'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
      })
      if (isOpenInModal) isOpenInModal.handleOnClose()
    }
  }

  const loadPromoters = useCallback(async () => {
    try {
      const responsePromoters = await api.get(apiListPromotersOptions())
      setPromotersOptions(
        responsePromoters.data?.map(
          (item: { name: string; cpf: string; id: number }) => ({
            name: `${item.cpf} ${item.name}`,
            value: item.id
          })
        )
      )
    } catch (error) {}
  }, [])

  useEffect(() => {
    loadPromoters()
  }, [loadPromoters])

  const removeRouteAgency = (id: string | number) => {
    if (!routeAgencies.some(item => item.show === true && item.id !== id)) {
      return
    }
    setRouteAgencies(prev =>
      prev.map(item => ({ ...item, show: item.id === id ? false : item.show }))
    )
  }

  const addRouteAgency = () => {
    setRouteAgencies(prev => [
      ...prev,
      {
        id: v4(),
        priority: ROUTE_PRIORITY_ENUM.LOW,
        show: true
      }
    ])
  }
  return (
    <Form onSubmit={onSubmitForm} defaultValues={defaultValues}>
      <>
        <div className="card mb-5 mb-xl-10">
          <FormContainer className="form">
            <div className="row mb-5">
              <SelectSearch
                name="route.promoter_id"
                label="Promotor"
                className="col-md-4"
                options={promotersOptions}
                blank
                defaultValue=""
                controlled
                placeholder="Digite para buscar promotor..."
                noOptionsMessage="Nenhum promotor encontrado"
                rules={{ required: true }}
                onSelectCallback={option => {
                  if (option?.value) {
                    setDefaultValues(prev => {
                      return {
                        route: {
                          ...prev.route,
                          promoter_id: Number(option.value)
                        },
                        routeAgencies: [
                          {
                            priority: ROUTE_PRIORITY_ENUM.LOW,
                            show: true
                          }
                        ]
                      }
                    })
                    setRouteAgencies([
                      {
                        id: v4(),
                        priority: ROUTE_PRIORITY_ENUM.LOW,
                        show: true
                      }
                    ])
                    loadAgencies(Number(option.value))
                  }
                }}
              />
              <Input
                name="route.name"
                label="Nome"
                rules={{ required: true }}
                className="col-md-8"
                controlled
              />
              <Select
                name="route.priority"
                label="Prioridade"
                rules={{ required: true }}
                className="col-md-4"
                options={Object.entries(ROUTE_PRIORITY_MAP).map(
                  ([key, label]) => ({
                    name: label,
                    value: key
                  })
                )}
                controlled
                blank
              />
              <Select
                name="route.status"
                label="Status"
                rules={{ required: true }}
                className="col-md-4"
                options={Object.entries(ROUTE_STATUS_MAP).map(
                  ([key, label]) => ({
                    name: label,
                    value: key
                  })
                )}
                controlled
                blank
              />
              <DatePicker
                name="route.scheduled_date"
                className="col-md-4"
                label="Data"
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/AAAA"
                customInput={
                  <IMaskInput
                    mask={Date}
                    pattern={'d/m/Y'}
                    format={date => {
                      return moment(date).format(INPUT_MASK.DATE_LOCALE)
                    }}
                    parse={value => {
                      return moment(value, INPUT_MASK.DATE_LOCALE).toDate()
                    }}
                  />
                }
                onChange={value => setScheduledDate(value)}
                selected={scheduledDate}
                controlled
                rules={{ required: true }}
              />

              <Textarea
                name="route.observation"
                label="Observações da rota"
                className="col-md-8"
                controlled
              />
            </div>
            <h4 className="pt-4">Agências</h4>
            {routeAgencies.map(
              (routeAgency, index) =>
                routeAgency.show && (
                  <div className="row mb-5" key={routeAgency.id}>
                    <SelectSearch
                      name={`routeAgencies.${index}.agency_id`}
                      label="Agência"
                      className="col-md-4"
                      options={agenciesOptions}
                      blank
                      defaultValue=""
                      placeholder="Digite para buscar agência..."
                      noOptionsMessage="Nenhuma agência encontrada"
                      rules={{ required: true }}
                      controlled
                    />
                    <Select
                      name={`routeAgencies.${index}.priority`}
                      label="Prioridade"
                      rules={{ required: true }}
                      className="col-md-4"
                      options={Object.entries(ROUTE_PRIORITY_MAP).map(
                        ([key, label]) => ({
                          name: label,
                          value: key
                        })
                      )}
                      controlled
                      blank
                    />

                    <Textarea
                      name={`routeAgencies.${index}.observation`}
                      label="Observações"
                      className="col-md-8"
                    />

                    <div
                      className="col-md-1 d-flex align-items-end"
                      style={{ marginBottom: '74px' }}
                    >
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeRouteAgency(routeAgency.id)}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                )
            )}
            <div className="d-flex mb-5">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => addRouteAgency()}
              >
                Adicionar Agência
              </button>
            </div>
          </FormContainer>
          <div className="card-footer d-flex justify-content-end py-6 px-9">
            <button type="submit" className="btn btn-primary">
              Salvar
            </button>
          </div>
        </div>
      </>
    </Form>
  )
}
